import React from 'react';
import { RefreshCw, Zap } from 'lucide-react';

import { CustomSelect } from '../CustomElements';
import type { PricePresentation } from '../../market/pricePresentation';
import { formatCurrency } from '../../utils/currency';

const ORDER_TYPE_OPTIONS = [
  { value: 'MARKET', label: 'MKT' },
  { value: 'LIMIT', label: 'LMT' },
  { value: 'MIDPRICE', label: 'MID' },
  { value: 'STOP', label: 'STP' },
  { value: 'TRAIL', label: 'TRAIL' },
];

interface OrderPricingFieldsProps {
  orderType: string;
  price: string;
  trailingAmount: string;
  trailType: 'amount' | 'percent';
  action: 'BUY' | 'SELL';
  marketPrice: number | null;
  fetchingPrice: boolean;
  pricePresentation: PricePresentation;
  currency: string;
  onOrderTypeChange: (value: string) => void;
  onPriceChange: (value: string) => void;
  onTrailingAmountChange: (value: string) => void;
  onTrailTypeChange: (value: 'amount' | 'percent') => void;
  onRefreshQuote: () => void;
  onSnapToMarket: () => void;
}

const OrderPricingFields: React.FC<OrderPricingFieldsProps> = ({
  orderType,
  price,
  trailingAmount,
  trailType,
  action,
  marketPrice,
  fetchingPrice,
  pricePresentation,
  currency,
  onOrderTypeChange,
  onPriceChange,
  onTrailingAmountChange,
  onTrailTypeChange,
  onRefreshQuote,
  onSnapToMarket,
}) => (
  <div className="flex gap-3 items-end">
    <div className="flex-1">
      <label className="block text-[10px] font-bold text-muted uppercase mb-1 tracking-wider opacity-70">Type</label>
      <CustomSelect
        value={orderType}
        onChange={onOrderTypeChange}
        options={ORDER_TYPE_OPTIONS}
        ariaLabel="Order type"
      />
    </div>

    <div className="flex-[1.5]">
      <div className="flex justify-between items-end mb-1">
        <label className={`block text-[10px] font-bold uppercase tracking-wider ${orderType !== 'MARKET' ? 'text-neon opacity-90' : 'text-muted opacity-40'}`}>
          {orderType === 'STOP' ? 'Stop' : orderType === 'TRAIL' ? 'Trailing Amt' : orderType === 'MIDPRICE' ? 'Midpoint Cap' : 'Price'}
        </label>
        {marketPrice !== null ? (
          <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-sm bg-white/3 border border-white/5">
            <button
              type="button"
              onClick={onRefreshQuote}
              disabled={fetchingPrice}
              className={`transition-all ${fetchingPrice ? 'animate-spin opacity-50' : 'hover:text-neon text-muted/60'}`}
            >
              <RefreshCw className="w-2.5 h-2.5" />
            </button>
            <span className="text-[9px] font-bold text-muted/60 uppercase tracking-tighter">Mkt</span>
            <span className={`text-[10px] font-mono font-bold ${fetchingPrice ? 'opacity-40' : pricePresentation.priceClassName}`}>
              {marketPrice.toFixed(2)}
            </span>
            <span className={`rounded-sm border px-1 text-[8px] font-bold uppercase ${pricePresentation.badgeClassName}`}>
              {pricePresentation.status}
            </span>
            {orderType !== 'MARKET' && (
              <button
                type="button"
                onClick={onSnapToMarket}
                disabled={!pricePresentation.usableForPricing}
                title={pricePresentation.usableForPricing ? 'Snap to Market' : 'Stale or frozen prices cannot populate an order'}
                className="ml-0.5 text-muted hover:text-neon transition-colors disabled:cursor-not-allowed disabled:opacity-25"
              >
                <Zap className="w-2.5 h-2.5 fill-current" />
              </button>
            )}
          </div>
        ) : (
          fetchingPrice && <RefreshCw className="w-2.5 h-2.5 text-muted animate-spin mb-0.5" />
        )}
      </div>
      <div className="relative">
        {orderType === 'TRAIL' ? (
          <div className="flex gap-2 w-full">
            <input
              type="number"
              value={trailingAmount}
              onChange={(event) => onTrailingAmountChange(event.target.value)}
              onBlur={() => trailingAmount && onTrailingAmountChange(parseFloat(trailingAmount).toFixed(2))}
              placeholder="0.00"
              step="0.01"
              className="flex-1 bg-white/3 border border-white/10 focus:border-neon/50 rounded-lg px-3 py-2 text-foreground transition h-9 text-xs focus:outline-hidden font-mono"
              required
            />
            <div className="flex-shrink-0 flex rounded-lg bg-white/5 p-0.5 border border-white/10 h-9">
              <button
                type="button"
                onClick={() => onTrailTypeChange('amount')}
                className={`px-2.5 rounded-md text-[10px] font-black transition-all ${trailType === 'amount'
                  ? 'bg-neon text-black shadow-[0_0_10px_rgba(0,255,153,0.3)]'
                  : 'text-white/50 hover:text-white'
                  }`}
              >
                $
              </button>
              <button
                type="button"
                onClick={() => onTrailTypeChange('percent')}
                className={`px-2.5 rounded-md text-[10px] font-black transition-all ${trailType === 'percent'
                  ? 'bg-neon text-black shadow-[0_0_10px_rgba(0,255,153,0.3)]'
                  : 'text-white/50 hover:text-white'
                  }`}
              >
                %
              </button>
            </div>
          </div>
        ) : (
          <>
            <input
              type="number"
              value={price}
              onChange={(event) => onPriceChange(event.target.value)}
              onBlur={() => price && onPriceChange(parseFloat(price).toFixed(2))}
              placeholder={orderType !== 'MARKET'
                ? orderType === 'MIDPRICE' ? 'Max buy / min sell' : '0.00'
                : pricePresentation.usableForPricing && marketPrice
                  ? marketPrice.toFixed(2)
                  : 'Market'}
              disabled={orderType === 'MARKET'}
              step="0.01"
              className={`w-full bg-white/3 border rounded-lg px-3 py-2 text-foreground transition h-9 text-xs focus:outline-hidden ${orderType !== 'MARKET'
                ? 'border-white/10 focus:border-neon/50'
                : 'border-transparent opacity-30 cursor-not-allowed placeholder-muted/50 font-bold'
                }`}
              required={orderType !== 'MARKET'}
            />
            {orderType === 'MARKET' && marketPrice && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-40">
                <span className={`text-[10px] font-bold ${pricePresentation.priceClassName}`}>
                  {pricePresentation.status}
                </span>
              </div>
            )}
          </>
        )}
      </div>
      {orderType === 'TRAIL' && pricePresentation.usableForPricing && marketPrice && trailingAmount && !isNaN(parseFloat(trailingAmount)) && (
        <div className="mt-1.5 text-[11px] font-bold text-white/50 flex justify-between items-center bg-white/2 border border-white/5 rounded-md px-2 py-1 select-none">
          <span className="uppercase tracking-wider text-[9px] text-muted">Est. Stop Price:</span>
          <span className="font-mono text-rose-400 font-black">
            {formatCurrency(
              trailType === 'amount'
                ? (action === 'BUY' ? marketPrice + parseFloat(trailingAmount) : marketPrice - parseFloat(trailingAmount))
                : (action === 'BUY' ? marketPrice * (1 + parseFloat(trailingAmount) / 100) : marketPrice - parseFloat(trailingAmount) / 100),
              currency,
            )}
          </span>
        </div>
      )}
    </div>
  </div>
);

export default OrderPricingFields;
